import {Component, ElementRef, EventEmitter, forwardRef, HostListener, Input, Output} from '@angular/core';
import {FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule} from '@angular/forms';
import {CommonModule} from '@angular/common';
import {BaseCustomInputComponent} from '../base-custom-input.component';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatTooltipModule} from '@angular/material/tooltip';
import {
  buildHueSliderGradient,
  getPredefinedSwatches,
  GRAY_SWATCH,
  hueFromHex,
  isSameColor,
  normalizedColorForHue
} from '@maporium-workspace/shared';
import {CytoscapeService} from '../../../services/cy-services/cytoscape.service';
import {MapService} from '../../../api/services/map.service';

/**
 * MAP-852: color picker used by the sidebar styles section (node/link/map-info)
 * and the user profile dialog. Previously a thin wrapper around ngx-color's
 * `color-block` (fixed swatches only, no hue selection, hex input hidden via
 * CSS). Rebuilt as a self-contained hue-slider + swatch grid so we can:
 *  - offer a continuous hue slider (AC1/AC2/AC3), rendered with the SAME
 *    luminance-normalization formula used everywhere else in the app
 *    (`@maporium-workspace/shared`, single source of truth - also used by
 *    the AI color-generation constraint, see `resolveAiColor`).
 *  - recreate the swatches from that formula (AC4).
 *  - highlight whichever swatch matches the current color (AC6).
 *  - append swatches for any non-predefined color already used in the open
 *    smap, read live from the Cytoscape graph + selected map styles (AC8);
 *    opt-out via `[showSmapColors]="false"` for pickers that don't edit
 *    anything in the smap (the profile dialog's badge color).
 */
@Component({
  selector: 'app-color-input',
  standalone: true,
  template: `
    <div class="row align-items-center no-gutter">
      <!-- Label block if provided -->
      <div class="col-12 mb-1" *ngIf="label && !icon">
        <label class="input-label">{{ label }}</label>
      </div>

      <!-- Icon block (only if no label is set) -->
      <div class="col-2 justify-content-center align-items-center d-flex" *ngIf="icon && !label"
           [matTooltip]="placeholder">
        <span class="material-symbols-rounded"><label>{{ icon }}</label></span>
      </div>

      <!-- Value Input -->
      <div class="px-0" [ngClass]="{'col-10': icon && !label, 'col-12': label || !icon}">
        <div class="outer-box" tabindex="0" (keydown)="handleKeydown($event)">
          <div class="inner-box" [style.background-color]="value" (click)="togglePicker()"></div>
        </div>
      </div>
    </div>

    <input type="text"
           class="w-100 key-input disabled-input"
           [value]="value"
           hidden
           [formControl]="formControl"/>

    <div class="color-picker-panel" *ngIf="showColorPicker">
      <!-- AC1/AC3: hue slider, gradient built from the same normalized-luminance formula as the swatches -->
      <div class="hue-slider-wrap">
        <div class="hue-slider-track" [style.background]="hueGradientCss"></div>
        <input type="range"
               class="hue-slider"
               min="0"
               max="359"
               step="1"
               [value]="currentHue"
               [style.--hue-thumb-color]="thumbColor"
               (input)="onHueInput($event)"/>
      </div>

      <!-- AC4/AC6/AC8: normalized swatches + any custom colors already used in this smap -->
      <div class="swatches">
        <button type="button"
                class="swatch"
                *ngFor="let c of allSwatches"
                [style.background-color]="c"
                [attr.title]="c"
                (click)="selectColor(c)">
          <!-- Selection is the checkmark alone: no frame, no ring, no shadow. -->
          <svg class="check" *ngIf="isSelected(c)" viewBox="0 0 24 24" aria-hidden="true">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
        </button>
      </div>
    </div>
  `,
  styleUrls: ['./color-input.component.scss'],
  imports: [FormsModule, ReactiveFormsModule, CommonModule, MatFormFieldModule, MatTooltipModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ColorInputComponent),
      multi: true,
    },
  ],
})
export class ColorInputComponent extends BaseCustomInputComponent {
  /** Recreated per MAP-852 AC4: 8 normalized hues + the medium-gray anchor. Overridable, but no call site does anymore. */
  @Input() colors = getPredefinedSwatches();
  @Input() placeholder = '';
  @Input() label = '';

  @Input() value = '';
  @Input() icon = '';
  /**
   * AC8 applies to pickers that edit something inside the open smap. Set false
   * where the picked color has nothing to do with the smap (the user profile
   * dialog's badge color) so only the nine predefined swatches are offered.
   */
  @Input() showSmapColors = true;
  @Output() colorChange = new EventEmitter<{ color: { hex: string } }>();

  showColorPicker = false;

  /** Colors used elsewhere in the currently open smap that aren't already predefined (AC8). Recomputed each time the picker opens. */
  usedColors: string[] = [];

  readonly hueGradientCss = buildHueSliderGradient();

  constructor(
    private elRef: ElementRef,
    private cyService: CytoscapeService,
    private mapService: MapService
  ) {
    super();
  }

  get currentHue(): number {
    return Math.round(hueFromHex(this.value || GRAY_SWATCH));
  }

  /** Solid fill for the slider thumb: the exact color its current position maps to. */
  get thumbColor(): string {
    return normalizedColorForHue(this.currentHue);
  }

  get allSwatches(): string[] {
    return [...this.colors, ...this.usedColors];
  }

  @HostListener('document:click', ['$event'])
  handleDocumentClick(event: MouseEvent) {
    // Check if the click target is outside the component's element
    if (!this.elRef.nativeElement.contains(event.target)) {
      this.closeColorPicker();
    }
  }

  @HostListener('document:keydown.escape')
  handleEscape() {
    if (this.showColorPicker) {
      this.closeColorPicker();
    }
  }

  togglePicker(): void {
    this.showColorPicker = !this.showColorPicker;
    if (this.showColorPicker) {
      this.refreshUsedColors();
    }
  }

  closeColorPicker() {
    this.showColorPicker = false;
  }

  handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.togglePicker();
    }
  }

  isSelected(hex: string): boolean {
    return isSameColor(hex, this.value);
  }

  /**
   * Applies a swatch WITHOUT closing the panel, so the user can keep trying
   * colors. The panel still closes on outside click, Escape, or by toggling
   * the trigger - the same paths the hue slider already relied on.
   */
  selectColor(hex: string): void {
    this.applyColor(hex);
  }

  onHueInput(event: Event): void {
    const hue = Number((event.target as HTMLInputElement).value);
    this.applyColor(normalizedColorForHue(hue));
  }

  /**
   * Single write path for both the slider and the swatches. `value` is an
   * @Input, but it is also the local source of truth for the preview box, the
   * slider thumb and the checkmark, so it is updated immediately instead of
   * waiting for the parent form to persist and push the binding back.
   */
  private applyColor(hex: string): void {
    this.value = hex;
    this.formControl.setValue(hex);
    this.colorChange.emit({color: {hex}});
  }

  /**
   * AC8: derives "colors used elsewhere in this smap" from the live Cytoscape
   * graph (node/edge `color` data, same source the canvas itself renders from)
   * plus the current map's own background color. Recomputed on open rather
   * than kept reactively in sync - "reasonably in sync" per the ticket, and
   * avoids adding subscription plumbing to a leaf form control.
   */
  private refreshUsedColors(): void {
    if (!this.showSmapColors) {
      this.usedColors = [];
      return;
    }

    const predefined = new Set(this.colors.map(c => c.toLowerCase()));
    const found = new Set<string>();

    const cy = this.cyService.cy;
    if (cy) {
      cy.nodes().forEach(node => {
        const color = node.data('color');
        if (color) found.add(String(color).toLowerCase());
      });
      cy.edges().forEach(edge => {
        const color = edge.data('color');
        if (color) found.add(String(color).toLowerCase());
      });
    }

    const mapColor = this.mapService.selectedMap()?.styles?.color;
    if (mapColor) found.add(String(mapColor).toLowerCase());

    this.usedColors = Array.from(found).filter(c => !predefined.has(c));
  }
}
